import { useState, useEffect } from 'react';
import { Accordion } from '@ds';
import { useUserProfileStore } from 'src/stores/UserProfile';
import { CheckIsEmptyAddressItem, processUpdatedDetails } from 'src/pages/UserProfile/UserProfile.service';
import { AddressType, ContactAddress } from 'src/shared/types/User';
import Address from './Address';

interface AddressesProp {
  contactRefId: number;
  addresses: ContactAddress[];
}

const INITIAL_CONTACT_ADDRESS: ContactAddress = {
  addressType: AddressType.EMPTY,
  country: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  suite: '',
  state: '',
  zipCode: '',
};

const AddressFields = ['addressType', 'country', 'addressLine1', 'addressLine2', 'city', 'suite', 'state', 'zipCode'];

const Addresses = (props: AddressesProp) => {
  const { addresses, contactRefId } = props;
  const { editMode, updatedFields, setUpdatedFields: setUpdatedFields } = useUserProfileStore();
  const defaultAddreses = addresses.length ? addresses : [{ ...INITIAL_CONTACT_ADDRESS }];
  const [contactAddresses, setContactAddresses] = useState<ContactAddress[]>([...defaultAddreses]);
  const [activeIndexes, setActiveIndexes] = useState<number[]>([0]);

  useEffect(() => {
    if (editMode) {
      const updatedAddresses = processUpdatedDetails(
        contactAddresses,
        addresses.map((address) => ({ ...address })),
        { contactRefId },
        'contactAddressId',
        AddressFields
      );
      const newFields = { ...updatedFields };
      const hasUpdatedAddresses = Object.values(updatedAddresses).some((list) => list?.length > 0);
      if (hasUpdatedAddresses) {
        newFields.contactAddresses = updatedAddresses;
      } else {
        delete newFields.contactAddresses;
      }
      setUpdatedFields(Object.keys(newFields)?.length > 0 ? newFields : null);
    }
  }, [editMode, contactRefId, addresses, contactAddresses]);

  const updateProperty = (index: number, fieldName: string, value: string | number) => {
    const updatedContactAddresses = [...contactAddresses];
    updatedContactAddresses[index] = {
      ...updatedContactAddresses[index],
      [fieldName]: value || '',
    };
    setContactAddresses(updatedContactAddresses);
  };

  const handleAddAnotherAddress = () => {
    setActiveIndexes([contactAddresses.length]);
    setContactAddresses((prevState) => [...prevState, { ...INITIAL_CONTACT_ADDRESS }]);
  };

  const handleDeleteAddress = (index: number) => {
    const updatedContactAddresses = [...contactAddresses];
    updatedContactAddresses.splice(index, 1);
    setContactAddresses(updatedContactAddresses);
    if (updatedContactAddresses.length === 1) {
      setActiveIndexes([0]);
    }
  };

  const addressesTabs = contactAddresses.map((address, index) => {
    const refAddress =
      defaultAddreses.find((item) => item.contactAddressId === address.contactAddressId) || INITIAL_CONTACT_ADDRESS;
    return {
      body: (
        <Address
          refAddress={refAddress as ContactAddress}
          onFieldChange={(fieldName, value) => updateProperty(index, fieldName, value)}
          onOptionChange={(fieldName, selectedOption) => updateProperty(index, fieldName, selectedOption?.value)}
          disabled={!editMode}
        />
      ),
    };
  });

  return (
    <Accordion
      multiple
      allowToDelete={true}
      isDeleteDisabled={!editMode}
      activeIndex={activeIndexes}
      onTabChange={({ index }) => setActiveIndexes(index as number[])}
      shouldPreserveFirstTab
      tabs={addressesTabs}
      onDeleteTab={handleDeleteAddress}
      tabHeaderPrefix="ADDRESSES"
      additionalTabConfig={{
        buttonLabel: 'ADDRESS',
        onAdd: handleAddAnotherAddress,
        disableAdd: !editMode || contactAddresses.some(CheckIsEmptyAddressItem),
      }}
    />
  );
};

export default Addresses;
